import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { UsersIcon, UserPlusIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { searchUsers } from '../services/userService';
import { createShare, listShares, changeShareRole, revokeShare, syncTrackCollaboration, type TrackShare, type UserSearchResult } from '../services/shareService';
import { getTrackById } from '../services/trackService';
import { type Track } from '../services/trackTypes';
import { useAuth } from '../contexts/AuthContext';
import { getTierLimits } from '../constants/tierLimits';
import toast from 'react-hot-toast';
import { logInfo, logError } from '../services/logtail';
import CassetteLoader from '../components/CassetteLoader';
import { sanitizeSearchQuery } from '../utils/sanitization';
import StorageRequiredBanner from '../components/StorageRequiredBanner';
import { useStorageGate } from '../hooks/useStorageGate';

const TrackCollaborators: React.FC = () => {
  const { t } = useTranslation();
  const { id: trackId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, userType, storageProvider } = useAuth();
  const { canShare } = useStorageGate();
  
  const [track, setTrack] = useState<Track | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [selectedRole, setSelectedRole] = useState<'viewer' | 'editor' | 'musician' | 'sound_engineer' | 'content_maker'>('viewer');
  const [shares, setShares] = useState<TrackShare[]>([]);
  const [loading, setLoading] = useState(false);
  // const [updatingRoles, setUpdatingRoles] = useState<Set<string>>(new Set());
  const [searching, setSearching] = useState(false);
  const [pendingRoleChanges, setPendingRoleChanges] = useState<Record<string, 'viewer' | 'editor' | 'musician' | 'sound_engineer' | 'content_maker'>>({});
  const [collaboratorUsers, setCollaboratorUsers] = useState<Record<string, UserSearchResult>>({});
  const [isOwner, setIsOwner] = useState(false);
  const [userRole, setUserRole] = useState<'owner' | 'editor' | 'viewer' | 'musician' | 'sound_engineer' | 'content_maker'>('viewer');
  const [canInvite, setCanInvite] = useState(false);
  const [canManageRoles, setCanManageRoles] = useState(false);
  const [canRevoke, setCanRevoke] = useState(false);
  const [revokingUsers, setRevokingUsers] = useState<Set<string>>(new Set());
  const [trackOwner, setTrackOwner] = useState<UserSearchResult | null>(null);
  const [showRoleTooltip, setShowRoleTooltip] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const providerLabel = (provider: string) =>
    t(`storage.providers.${provider}`, provider);

  const shareStatusLabel = (share: TrackShare) => {
    const status = share.status || 'active';
    if (status === 'active') return t('sharing.status.active', 'Active');
    if (status === 'pending') return t('sharing.status.pending', 'Pending (not linked)');
    if (share.inactiveReason === 'provider_mismatch') {
      return t('sharing.status.filesUnavailableCrossCloud', 'Files unavailable (different cloud)');
    }
    if (share.inactiveReason === 'owner_auth_invalid') {
      return t('sharing.status.ownerAuthInvalid', 'Files unavailable (owner must reconnect cloud)');
    }
    if (share.inactiveReason === 'collaborator_auth_invalid') {
      return t('sharing.status.collaboratorAuthInvalid', 'Files unavailable (collaborator must reconnect)');
    }
    if (share.inactiveReason === 'acl_failed') {
      return t('sharing.status.aclFailed', 'Files unavailable (sharing setup failed)');
    }
    return t('sharing.status.filesUnavailable', 'Files unavailable');
  };

  const crossCloudInvite =
    !!selectedUser &&
    !!track?.storageProvider &&
    !(selectedUser.linkedProviders?.includes(track.storageProvider) ||
      selectedUser.storageProvider === track.storageProvider);

  const handleSyncSharing = async () => {
    if (!trackId) return;
    setSyncing(true);
    try {
      const summary = await syncTrackCollaboration(trackId);
      toast.success(
        t('sharing.sync.success', {
          activated: summary.sharesActivated,
          deactivated: summary.sharesDeactivated,
          defaultValue: `Sharing synced (${summary.sharesActivated} activated, ${summary.sharesDeactivated} deactivated)`,
        }),
      );
      await loadShares();
    } catch {
      toast.error(t('sharing.sync.failed', 'Failed to sync sharing'));
    } finally {
      setSyncing(false);
    }
  };

  // Helper function to get initials from email
  const getInitials = (email: string): string => {
    if (!email) return '?';
    const parts = email.split('@')[0].split(/[._-]/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  };

  // Helper function to extract name from email
  const getNameFromEmail = (email: string): string => {
    if (!email) return '';
    const localPart = email.split('@')[0];
    // Convert dots, underscores, and hyphens to spaces and capitalize
    const name = localPart
      .replace(/[._-]/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
    return name;
  };


  useEffect(() => {
    if (trackId && user) {
      loadTrackAndShares();
    }
  }, [trackId, user, userType]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim().length > 0) {
        performSearch(searchQuery.trim());
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadTrackAndShares = async () => {
    if (!trackId || !user) {
      logError('Cannot load track: missing trackId or user', { trackId, hasUser: !!user });
      return;
    }
    
    try {
      logInfo('Loading track and shares for TrackCollaborators', { trackId, userId: user.uid });
      
      // Load track data
      const trackData = await getTrackById(trackId);
      setTrack(trackData);
      
      logInfo('Track loaded successfully', { 
        trackId, 
        trackName: trackData.name
      });
      
      // Load shares and determine permissions (pass track data)
      await loadShares(trackData);
      
    } catch (error) {
      logError('Failed to load track', { trackId, error });
      toast.error(t('errors.failedToLoadTrack'));
    }
  };

  // const loadTrack = async () => {
  //   if (!trackId || !user) {
  //     logError('Cannot load track: missing trackId or user', { trackId, hasUser: !!user });
  //     return;
  //   }
  //   
  //   try {
  //     logInfo('Loading track for TrackCollaborators', { trackId, userId: user.uid });
  //     const trackData = await getTrackById(trackId);
  //     setTrack(trackData);
  //   } catch (error) {
  //     logError('Failed to load track', { trackId, error });
  //     toast.error('Failed to load track');
  //   }
  // };

  const loadShares = async (trackData?: Track) => {
    if (!trackId || !user) return;
    try {
      logInfo('Loading shares for track', { trackId });
      const data = await listShares(trackId);
      
      // Handle null/undefined response from backend
      const sharesData = data || [];
      setShares(sharesData);
      
      // Use trackData parameter if provided, otherwise use track state
      const currentTrack = trackData || track;
      
      logInfo('Shares loaded successfully', { 
        trackId, 
        sharesCount: sharesData.length,
        userId: user.uid,
        trackUserId: currentTrack?.userId
      });
      
      // Determine ownership - check track.userId first, then shares
      // This matches the logic in TrackDetail.tsx
      let userIsOwner = false;
      let userRole = 'viewer';
      
      // First check if user is the track creator/owner
      if (currentTrack && currentTrack.userId === user.uid) {
        userIsOwner = true;
        userRole = 'owner';
      } else if (sharesData.length === 0) {
        // No shares exist - if user can access the track, they might be the owner
        // But we should check track.userId which we already did above
        // If track.userId doesn't match and no shares, user has no access
        userIsOwner = false;
        userRole = 'viewer';
      } else {
        // Check if user appears as ownerUserId in any share (they're the track owner)
        const userIsOwnerFromShares = sharesData.some(share => share.ownerUserId === user.uid);
        if (userIsOwnerFromShares) {
          userIsOwner = true;
          userRole = 'owner';
        } else {
          // User is a collaborator - find their share
          const userShare = sharesData.find(share => share.collaboratorUserId === user.uid);
          if (userShare) {
            userRole = userShare.role;
            userIsOwner = false;
          } else {
            // User has no access
            userRole = 'viewer';
            userIsOwner = false;
          }
        }
      }
      
      setIsOwner(userIsOwner);
      
      // Set granular permissions based on ownership and tier
      const ownerLimits = getTierLimits(userType);
      const canInviteCollaborators = userIsOwner && (ownerLimits.maxCollaborators === -1 || ownerLimits.maxCollaborators > 0);
      
      setCanInvite(canInviteCollaborators); // Only owners with collaboration tier can invite
      setCanManageRoles(userIsOwner); // Only owners can change roles
      setCanRevoke(userIsOwner); // Only owners can revoke access
      
      // Store user's role for UI filtering
      setUserRole(userRole);
      
      // Find the actual track owner
      // Extract owner details from API response (owner info is included in each share)
      // For filtered shares (viewers, musicians, etc.), owner info should still be in the share object
      if (sharesData.length > 0) {
        // Owner info should be in the share object (backend includes it for each share)
        const shareWithOwner = sharesData.find(share => share.owner);
        if (shareWithOwner && shareWithOwner.owner) {
          setTrackOwner(shareWithOwner.owner);
        } else {
          // Owner info not in share - log for debugging
          logInfo('Owner info not found in shares', { 
            trackId, 
            sharesCount: sharesData.length,
            hasOwnerUserId: !!sharesData[0]?.ownerUserId,
            userRole 
          });
        }
      } else if (userIsOwner && user) {
        // If user is the owner and no shares exist, use current user as owner
        setTrackOwner({
          id: user.uid,
          userId: user.uid,
          email: user.email || '',
          effectiveTier: userType || 'starter',
          isPremium: userType === 'artist' || userType === 'studio', // Required by interface
        });
      } else if (currentTrack && currentTrack.userId && currentTrack.userId !== user.uid) {
        // Track has an owner but it's not the current user and no shares returned
        // This shouldn't happen for collaborators, but handle gracefully
        logInfo('Track has owner but no shares returned', { 
          trackId, 
          trackUserId: currentTrack.userId,
          userRole 
        });
      }
      
      // Find user's share for logging
      const userShare = sharesData.find(share => share.collaboratorUserId === user.uid);
      
      logInfo('Access determined from shares', { 
        userIsOwner, 
        userRole: userRole,
        userShareRole: userShare?.role,
        canEdit: userIsOwner || userRole === 'editor',
        sharesCount: sharesData.length,
        hasOwnerInfo: !!trackOwner
      });
      
      // Extract user details from API response (collaborator info is already included)
      const usersMap: Record<string, UserSearchResult> = {};
      sharesData.forEach(share => {
        if (share.collaborator && share.collaboratorUserId) {
          usersMap[share.collaboratorUserId] = share.collaborator;
        }
      });
      setCollaboratorUsers(usersMap);
    } catch (error) {
      logError('Failed to load shares', { trackId, error });
      toast.error(t('errors.failedToLoadCollaborators'));
      // Set default values to prevent UI from breaking
      setShares([]);
      setIsOwner(false); // Assume no permissions if we can't load shares
      setUserRole('viewer'); // Assume viewer role if we can't load shares
      
      // No collaboration permissions if we can't verify ownership
      setCanInvite(false);
      setCanManageRoles(false);
      setCanRevoke(false);
      setTrackOwner(null);
    }
  };

  const performSearch = async (query: string) => {
    setSearching(true);
    try {
      // Sanitize the search query first
      const sanitizedQuery = sanitizeSearchQuery(query);
      
      // Parse profile link if provided
      let searchTerm = sanitizedQuery;
      const profileLinkMatch = sanitizedQuery.match(/\/u\/([^/\s]+)/);
      if (profileLinkMatch) {
        searchTerm = profileLinkMatch[1];
      }

      const results = await searchUsers(searchTerm);
      // Convert UserSearchResult to display format
      const convertedResults = results.map(user => ({
        id: user.userId,
        userId: user.userId,
        email: user.email,
        effectiveTier: user.effectiveTier,
        isPremium: user.isPremium || false, // Required by interface
      }));
      setSearchResults(convertedResults);
    } catch (error) {
      logError('User search failed', { query: sanitizeSearchQuery(query), error });
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const canShareWithUser = (user: UserSearchResult): boolean => {
    // Check if user is the track owner
    if (trackOwner && user.userId === trackOwner.userId) {
      return false;
    }
    
    // Check if user is already a collaborator
    const isAlreadyCollaborator = shares.some(share => share.collaboratorUserId === user.userId);
    return !isAlreadyCollaborator;
  };

  const handleInvite = async () => {
    if (!canShare) return;
    if (!selectedUser || !trackId) return;
    
    // Frontend validation
    if (!canShareWithUser(selectedUser)) {
      if (trackOwner && selectedUser.userId === trackOwner.userId) {
        toast.error(t('share.cannotShareWithOwner'));
      } else {
        toast.error(t('share.alreadyShared'));
      }
      return;
    }
    
    setLoading(true);
    try {
      const share = await createShare(trackId, selectedUser.userId, selectedRole);
      if (share.status === 'inactive') {
        const trackProvider = track?.storageProvider || track?.requiredStorageProvider || storageProvider;
        const ownerProvider =
          trackProvider === 'cassette_s3' || trackProvider === 'none' || !trackProvider
            ? t('sharing.invite.externalCloud', 'an external cloud')
            : providerLabel(trackProvider);
        toast.success(
          t('sharing.invite.crossCloudSuccess', {
            name: getNameFromEmail(selectedUser.email) || selectedUser.email,
            provider: ownerProvider,
            defaultValue: `${getNameFromEmail(selectedUser.email) || selectedUser.email} was added. They can access files once they link ${ownerProvider} in Settings.`,
          }),
          { duration: 6000 },
        );
      } else {
        toast.success(t('share.inviteSent'));
      }
      setSelectedUser(null);
      setSearchQuery('');
      setSearchResults([]);
      await loadShares();
    } catch (error: any) {
      const errorCode = error.response?.data?.errorCode;
      if (errorCode === 'editor_requires_premium') {
        toast.error(t('share.editorRequiresPremium'));
      } else if (errorCode === 'collaborator_limit_reached') {
        toast.error(t('share.collaboratorLimitReached'));
      } else if (errorCode === 'cannot_share_with_self') {
        toast.error(t('share.cannotShareWithSelf'));
      } else if (errorCode === 'share_already_exists') {
        toast.error(t('share.alreadyShared'));
      } else if (errorCode === 'drive_link_required' || errorCode === 'storage_link_required') {
        toast.error(t('share.driveLinkRequired'));
        navigate('/auth/storage');
      } else {
        toast.error(t('share.inviteFailed'));
      }
      logError('Failed to create share', { trackId, collaboratorUserId: selectedUser.userId, error });
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (collaboratorUserId: string, newRole: 'viewer' | 'editor' | 'musician' | 'sound_engineer' | 'content_maker') => {
    setLoading(true);
    try {
      await changeShareRole(trackId!, collaboratorUserId, newRole);
      toast.success(t('share.roleChanged'));
      setPendingRoleChanges(prev => {
        const updated = { ...prev };
        delete updated[collaboratorUserId];
        return updated;
      });
      await loadShares();
    } catch (error: any) {
      const errorCode = error.response?.data?.code;
      if (errorCode === 'editor_requires_premium') {
        toast.error(t('share.editorRequiresPremium'));
      } else if (errorCode === 'collaborator_limit_reached') {
        toast.error(t('share.collaboratorLimitReached'));
      } else {
        toast.error(t('share.roleChangeFailed'));
      }
      logError('Failed to change role', { trackId, collaboratorUserId, newRole, error });
    } finally {
      setLoading(false);
    }
  };


  const handleRevoke = async (collaboratorUserId: string) => {
    if (!trackId) return;
    if (!window.confirm(t('share.confirmRevoke'))) {
      return;
    }

    // Add user to revoking set
    setRevokingUsers(prev => new Set(prev).add(collaboratorUserId));

    try {
      await revokeShare(trackId, collaboratorUserId);
      toast.success(t('share.accessRevoked'));
      await loadShares();
    } catch (error) {
      toast.error(t('share.revokeFailed'));
      logError('Failed to revoke share', { trackId, collaboratorUserId, error });
    } finally {
      // Remove user from revoking set
      setRevokingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(collaboratorUserId);
        return newSet;
      });
    }
  };

  if (!trackId) {
    return (
      <div className="min-h-screen bg-[var(--color-bg)] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[var(--color-text)] mb-2">Track Not Found</h1>
          <p className="text-[var(--color-muted)] mb-4">The requested track could not be found.</p>
          <Link to="/dashboard" className="hover:opacity-80" style={{ color: 'var(--collab-blue-text)' }}>
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg)]">
      {/* Header */}
      <div className="bg-[var(--color-surface)] border-b border-[var(--color-border)]">
        <div className="px-4 py-6">
          <div className="flex items-center gap-4 mb-4">
          </div>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--collab-blue-bg)' }}>
              <UsersIcon className="w-6 h-6" style={{ color: 'var(--collab-blue-text)' }} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[var(--color-text)]">
                {t('share.manageCollaborators')}
              </h1>
              <p className="text-[var(--color-muted)]">
                {track ? track.name : t('common.loading')}
              </p>
            </div>
            {isOwner && (
              <button
                type="button"
                onClick={handleSyncSharing}
                disabled={syncing}
                className="ml-auto text-xs px-3 py-1.5 rounded-lg border border-[var(--color-border)] text-[var(--color-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-hover)] disabled:opacity-50"
              >
                {syncing ? t('sharing.sync.running', 'Syncing…') : t('sharing.sync.button', 'Sync sharing')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-8">
        {/* Current Limitation Notice */}
        <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: 'var(--info-blue-bg)' }}>
              <svg className="w-4 h-4" style={{ color: 'var(--info-blue-text)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-[var(--color-text)] font-medium mb-1">
                {t('share.currentLimitationTitle')}
              </p>
              <p className="text-sm text-[var(--color-muted)]">
                {t('share.currentLimitation')}
              </p>
            </div>
          </div>
        </div>

        {/* Collaboration Limit Warning - Only show when limit is reached */}
        {(() => {
          const ownerLimits = getTierLimits(userType);
          const hasReachedLimit = ownerLimits.maxCollaborators !== -1 && shares.length >= ownerLimits.maxCollaborators;
          const isStarter = userType === 'starter';
          
          return (isStarter || hasReachedLimit) && isOwner && (
            <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-6 mb-8">
              <h2 className="text-lg font-semibold text-[var(--color-text)] mb-3 flex items-center gap-2">
                <UsersIcon className="w-5 h-5" />
                {t(`share.collaborationInfo.${userType}.title`)}
              </h2>
              <p className="text-[var(--color-muted)] mb-4">
                {t(`share.collaborationInfo.${userType}.message`)}
              </p>
              {userType !== 'starter' && (
                <div className="text-sm text-[var(--color-muted)] bg-[var(--color-hover)] rounded-lg px-3 py-2 inline-block">
                  <strong>Limit:</strong> {t(`share.collaborationInfo.${userType}.limitInfo`)} ({shares.length}/{ownerLimits.maxCollaborators} used)
                </div>
              )}
              {userType === 'starter' && (
                <div className="mt-4">
                  <button
                    onClick={() => navigate('/upgrade')}
                    className="btn-primary"
                  >
                    {t('share.collaborationInfo.starter.upgradeButton')}
                  </button>
                </div>
              )}
            </div>
          );
        })()}

        {/* Near Limit Warning - Show when close to limit but not reached */}
        {(() => {
          const ownerLimits = getTierLimits(userType);
          const isNearLimit = ownerLimits.maxCollaborators !== -1 && 
                             shares.length > 0 && 
                             shares.length >= ownerLimits.maxCollaborators - 1 && 
                             shares.length < ownerLimits.maxCollaborators;
          
          return isNearLimit && isOwner && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                <p className="text-sm text-yellow-800">
                  <strong>Near limit:</strong> You have {shares.length}/{ownerLimits.maxCollaborators} collaborators. 
                  You can add {ownerLimits.maxCollaborators - shares.length} more.
                </p>
              </div>
            </div>
          );
        })()}

        {/* Invite Section - Only show if user can invite AND hasn't reached limit */}
        {!canShare && canInvite && (
          <div className="mb-8">
            <StorageRequiredBanner />
          </div>
        )}

        {canInvite && canShare && (() => {
          const ownerLimits = getTierLimits(userType);
          const hasReachedLimit = ownerLimits.maxCollaborators !== -1 && shares.length >= ownerLimits.maxCollaborators;
          return !hasReachedLimit;
        })() && (
          <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-6 mb-8">
          <h2 className="text-lg font-semibold text-[var(--color-text)] mb-4 flex items-center gap-2">
            <UserPlusIcon className="w-5 h-5" />
            {t('share.inviteNewCollaborator')}
          </h2>
          
          <label className="block text-sm font-medium text-[var(--color-text)] mb-2">
            {t('share.inviteUser')}
          </label>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('share.searchPlaceholder')}
            className="w-full px-4 py-2 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-from)]"
            data-testid="collaborator-search-input"
          />

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mt-2 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg max-h-48 overflow-y-auto">
              {searchResults.map((user) => {
                const isOwner = trackOwner && user.userId === trackOwner.userId;
                const isAlreadyCollaborator = shares.some(share => share.collaboratorUserId === user.userId);
                const canSelect = canShareWithUser(user);
                
                return (
                  <button
                    key={user.userId}
                    onClick={() => {
                      if (canSelect) {
                        setSelectedUser(user);
                        setSearchQuery('');
                        setSearchResults([]);
                      }
                    }}
                    disabled={!canSelect}
                    className={`w-full px-3 sm:px-4 py-3 text-left transition-colors border-b border-[var(--color-border)] last:border-0 flex items-center gap-2 sm:gap-3 ${
                      canSelect 
                        ? 'hover:bg-[var(--color-hover)] cursor-pointer' 
                        : 'opacity-50 cursor-not-allowed'
                    }`}
                  >
                    {/* Avatar */}
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white font-semibold text-xs sm:text-sm flex-shrink-0" style={{ backgroundImage: 'var(--brand-gradient-r)' }}>
                      {getInitials(user.email)}
                    </div>
                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-1.5 sm:gap-2 flex-wrap">
                        <span className="font-medium text-[var(--color-text)] text-sm sm:text-base break-all">{user.email}</span>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {user.effectiveTier && user.effectiveTier !== 'starter' && (
                            <span key="tier-badge" className="px-1.5 sm:px-2 py-0.5 text-xs font-medium rounded whitespace-nowrap" style={{ backgroundColor: 'var(--info-yellow-bg)', color: 'var(--info-yellow-text)' }}>
                              {user.effectiveTier === 'studio' ? 'Studio' : 'Artist'}
                            </span>
                          )}
                          {isOwner && (
                            <span key="owner-badge" className="px-1.5 sm:px-2 py-0.5 text-xs font-medium rounded whitespace-nowrap" style={{ backgroundColor: 'var(--info-green-bg)', color: 'var(--info-green-text)' }}>Owner</span>
                          )}
                          {isAlreadyCollaborator && (
                            <span key="shared-badge" className="px-1.5 sm:px-2 py-0.5 text-xs font-medium rounded whitespace-nowrap" style={{ backgroundColor: 'var(--collab-blue-bg)', color: 'var(--collab-blue-text)' }}>Already shared</span>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-[var(--color-muted)] break-all">{user.userId}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {searching && <div className="mt-2 text-sm text-[var(--color-muted)]">{t('share.searching')}</div>}
          {searchQuery && !searching && searchResults.length === 0 && (
            <div className="mt-2 text-sm text-[var(--color-muted)]">{t('share.noResults')}</div>
          )}

          {/* Role Explanation */}
          <div className="mt-4 p-3 rounded-lg text-sm" style={{ backgroundColor: 'var(--collab-blue-bg)' }}>
            <p className="font-medium text-[var(--color-text)] mb-1">{t('share.sharingPermissions')}</p>
            <ul className="text-xs space-y-1 text-[var(--color-muted)]">
              <li>• {t('share.viewerPermission')}</li>
              <li>• {t('share.editorPermission')}</li>
              <li>• {t('share.musicianPermission')}</li>
              <li>• {t('share.soundEngineerPermission')}</li>
              <li>• {t('share.contentMakerPermission')}</li>
            </ul>
          </div>

          {/* Selected User */}
          {selectedUser && (
            <div className="mt-4 p-3 sm:p-4 rounded-lg border-2" style={{ backgroundColor: 'var(--info-green-bg)', borderColor: 'var(--info-green-border)' }}>
              <div className="flex items-center gap-2 sm:gap-3 mb-3">
                {/* Avatar */}
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white font-semibold text-xs sm:text-sm flex-shrink-0" style={{ backgroundImage: 'var(--brand-gradient-r)' }}>
                  {getInitials(selectedUser.email)}
                </div>
                {/* User Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-1.5 sm:gap-2 flex-wrap">
                    <span className="font-medium text-[var(--color-text)] text-sm sm:text-base break-all">{selectedUser.email}</span>
                    {selectedUser.effectiveTier && selectedUser.effectiveTier !== 'starter' && (
                      <span className="px-1.5 sm:px-2 py-0.5 text-xs font-medium rounded whitespace-nowrap flex-shrink-0" style={{ backgroundColor: 'var(--info-yellow-bg)', color: 'var(--info-yellow-text)' }}>
                        {selectedUser.effectiveTier === 'studio' ? 'Studio' : 'Artist'}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[var(--color-muted)] break-all">{selectedUser.userId}</div>
                </div>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="text-[var(--color-muted)] hover:text-[var(--color-text)] flex-shrink-0"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-3">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label className="block text-xs font-medium text-[var(--color-text)]">Select Role</label>
                    <button
                      type="button"
                      onClick={() => setShowRoleTooltip(!showRoleTooltip)}
                      className="text-[var(--color-muted)] hover:text-[var(--color-text)] transition-colors"
                      aria-label="Role information"
                    >
                      <InformationCircleIcon className="w-4 h-4" />
                    </button>
                  </div>
                  {showRoleTooltip && (
                    <div className="mb-2 p-3 rounded-lg text-xs bg-[var(--collab-blue-bg)] text-[var(--color-text)]">
                      <p className="font-medium mb-2">{t('share.sharingPermissions')}</p>
                      <ul className="space-y-1.5 text-[var(--color-muted)]">
                        <li>• {t('share.viewerPermission')}</li>
                        <li>• {t('share.editorPermission')}</li>
                        <li>• {t('share.musicianPermission')}</li>
                        <li>• {t('share.soundEngineerPermission')}</li>
                        <li>• {t('share.contentMakerPermission')}</li>
                      </ul>
                    </div>
                  )}
                  <select
                    value={selectedRole}
                    onChange={(e) => {
                      const newRole = e.target.value as 'viewer' | 'editor' | 'musician' | 'sound_engineer' | 'content_maker';
                      // Check if destination user can be assigned editor role (only Editor requires Premium)
                      const effectiveTier = selectedUser?.effectiveTier || 'starter';
                      const canBeEditor = effectiveTier === 'artist' || effectiveTier === 'studio';
                      if (!canBeEditor && newRole === 'editor') {
                        toast.error(t('share.editorRequiresPremium'));
                        return;
                      }
                      // New roles (musician, sound_engineer, content_maker) can be assigned to Starter users
                      setSelectedRole(newRole);
                    }}
                    className="w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--brand-from)] focus:border-transparent"
                    title={
                      selectedRole === 'viewer' ? t('share.roleViewerTooltip') :
                      selectedRole === 'editor' ? t('share.roleEditorTooltip') :
                      selectedRole === 'musician' ? t('share.roleMusicianTooltip') :
                      selectedRole === 'sound_engineer' ? t('share.roleSoundEngineerTooltip') :
                      selectedRole === 'content_maker' ? t('share.roleContentMakerTooltip') :
                      ''
                    }
                  >
                    <option value="viewer" title={t('share.roleViewerTooltip')}>{t('share.roleViewer')}</option>
                    <option value="editor" disabled={(selectedUser?.effectiveTier || 'starter') === 'starter'} title={t('share.roleEditorTooltip')}>
                      {t('share.roleEditor')} {(selectedUser?.effectiveTier || 'starter') === 'starter' && '🔒'}
                    </option>
                    <option value="musician" title={t('share.roleMusicianTooltip')}>{t('share.roleMusician')}</option>
                    <option value="sound_engineer" title={t('share.roleSoundEngineerTooltip')}>{t('share.roleSoundEngineer')}</option>
                    <option value="content_maker" title={t('share.roleContentMakerTooltip')}>{t('share.roleContentMaker')}</option>
                  </select>
                </div>

                {crossCloudInvite && (
                  <div className="p-3 rounded-lg border border-amber-500/40 bg-amber-500/10 text-xs text-[var(--color-text)]">
                    {t('sharing.invite.crossCloudWarning', {
                      provider: providerLabel(track?.storageProvider || storageProvider),
                      defaultValue: `This person does not have ${providerLabel(track?.storageProvider || storageProvider)} linked. You can still invite them; they will get file access once they link that provider in Settings.`,
                    })}
                  </div>
                )}

                <button
                  onClick={handleInvite}
                  disabled={loading || !canShareWithUser(selectedUser) || !canShare}
                  className="w-full px-6 py-2.5 text-white rounded-lg disabled:opacity-50 transition-colors font-medium flex items-center justify-center gap-2"
                  style={{ backgroundColor: 'var(--brand-from)' }}
                  onMouseEnter={(e) => {
                    if (!loading && canShareWithUser(selectedUser)) {
                      const target = e.target as HTMLElement;
                      target.style.backgroundColor = 'var(--brand-hover-from)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    const target = e.target as HTMLElement;
                    target.style.backgroundColor = 'var(--brand-from)';
                  }}
                  data-testid="send-invite-button"
                >
                  {loading ? (
                    <>
                      <CassetteLoader size={48} />
                      Sending Invite...
                    </>
                  ) : (
                    t('share.sendInvite')
                  )}
                </button>
              </div>
            </div>
          )}
          </div>
        )}

        {/* Participants List */}
        <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-[var(--color-text)]">
              {t('share.participants')} ({shares.length})
            </h2>
          </div>

          {/* Owner Section */}
          <div className="mb-6" data-testid="owner-section">
            <h3 className="text-sm font-semibold text-[var(--color-text)] mb-3 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--info-green-border)' }}></div>
              {t('share.owner')}
            </h3>
            <div className="flex items-center gap-2 sm:gap-3 p-3 bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)]">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0" style={{ backgroundImage: 'linear-gradient(to bottom right, var(--info-green-border), var(--info-green-text))' }}>
                {trackOwner ? getInitials(trackOwner.email) : (isOwner ? getInitials(user?.email || '') : '?')}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-1.5 sm:gap-2 flex-wrap">
                  <div className="flex flex-col">
                    <span className="font-medium text-[var(--color-text)] text-sm">
                      {isOwner ? t('share.youOwnThisTrack') : (trackOwner ? getNameFromEmail(trackOwner.email) : t('common.loading'))}
                    </span>
                    {isOwner && user?.email && (
                      <span className="text-xs text-[var(--color-muted)] break-all">
                        {user.email}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-1.5 sm:gap-2 mt-1 flex-wrap">
                  <span className="text-xs px-2 py-0.5 rounded whitespace-nowrap flex-shrink-0" style={{ backgroundColor: 'var(--info-green-bg)', color: 'var(--info-green-text)' }}>
                    {t('share.owner')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Collaborators Section */}
          <div>
            <h3 className="text-sm font-semibold text-[var(--color-text)] mb-3 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--collab-blue-border)' }}></div>
              Other Collaborators ({shares.length})
            </h3>

            {loading || revokingUsers.size > 0 ? (
              <div className="text-center py-8 text-[var(--color-muted)]">
                <div className="flex items-center justify-center gap-2">
                  <CassetteLoader size={48} />
                  <span>Updating collaborators...</span>
                </div>
              </div>
            ) : shares.length === 0 ? (
              <div className="p-3 rounded-lg border" style={{ backgroundColor: 'var(--color-hover)', borderColor: 'var(--color-border)' }}>
                <p className="text-sm text-[var(--color-muted)]">
                  {userType === 'starter' 
                    ? "No collaborators yet. Upgrade to Artist or Studio to invite team members!"
                    : "No other collaborators yet. Use the form above to invite someone to collaborate on this track."
                  }
                </p>
              </div>
            ) : (() => {
              const ownerLimits = getTierLimits(userType);
              const hasReachedLimit = ownerLimits.maxCollaborators !== -1 && shares.length >= ownerLimits.maxCollaborators;
              return hasReachedLimit && isOwner; // Only show for owners
            })() ? (
              <div className="p-3 rounded-lg border border-orange-200 bg-orange-50">
                <p className="text-sm text-orange-800">
                  {userType === 'starter' ? (
                    <>
                      <strong>Collaboration not available:</strong> Starter users cannot invite collaborators. 
                      <Link to="/upgrade" className="ml-1 underline">Upgrade to Artist or Studio</Link> to share tracks with your team.
                    </>
                  ) : (
                    <>
                      <strong>Collaborator limit reached:</strong> You have reached your limit of {getTierLimits(userType).maxCollaborators} collaborators per track. 
                      {userType === 'artist' ? ' Upgrade to Studio for unlimited collaborators.' : ''}
                    </>
                  )}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {shares.filter((share) => {
                  // Filter collaborators based on user role
                  // Backend already filters shares, but we apply frontend filtering for consistency
                  if (userRole === 'owner') {
                    return true; // Owners see all collaborators
                  } else if (userRole === 'editor') {
                    return true; // Editors see all collaborators
                  } else {
                    // Viewers, musicians, sound_engineers, content_makers only see themselves
                    return share.collaboratorUserId === user?.uid;
                  }
                }).map((share) => {
                  // Use collaborator data directly from API response
                  const collaboratorUser = share.collaborator || collaboratorUsers[share.collaboratorUserId];
                  const pendingRole = pendingRoleChanges[share.collaboratorUserId];
                  const currentRole = share.role; // Original role from server
                  const displayRole = pendingRole || share.role; // What's shown in dropdown
                  
                  return (
                    <div
                      key={share.collaboratorUserId}
                      className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-[var(--color-bg)] rounded-lg border border-[var(--color-border)]"
                    >
                      <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0" style={{ backgroundImage: 'var(--brand-gradient-r)' }}>
                          {collaboratorUser ? getInitials(collaboratorUser.email) : '?'}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-1.5 sm:gap-2 flex-wrap">
                            <div className="flex flex-col">
                              <span className="font-medium text-[var(--color-text)] text-sm">
                                {collaboratorUser ? getNameFromEmail(collaboratorUser.email) : share.collaboratorUserId}
                              </span>
                              <span className="text-xs text-[var(--color-muted)] break-all">
                                {collaboratorUser ? collaboratorUser.email : ''}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {collaboratorUser?.effectiveTier && collaboratorUser.effectiveTier !== 'starter' && (
                                <span 
                                  key="subscription-badge" 
                                  className="px-1.5 py-0.5 text-xs font-medium rounded whitespace-nowrap flex-shrink-0" 
                                  style={{ backgroundColor: 'var(--info-yellow-bg)', color: 'var(--info-yellow-text)' }}
                                >
                                  {collaboratorUser.effectiveTier === 'studio' ? 'Studio' : 'Artist'}
                                </span>
                              )}
                              {pendingRole && (
                                <span key="modified-badge" className="px-1.5 py-0.5 text-xs font-medium rounded whitespace-nowrap flex-shrink-0" style={{ backgroundColor: 'var(--collab-blue-bg)', color: 'var(--collab-blue-text)' }}>Modified</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-start gap-1.5 sm:gap-2 mt-1 flex-wrap">
                            {userRole === 'owner' && (
                              <span className="text-xs text-[var(--color-muted)] font-mono break-all">
                                {collaboratorUser ? collaboratorUser.userId : share.collaboratorUserId}
                              </span>
                            )}
                            <span
                              className="text-xs px-2 py-0.5 rounded whitespace-nowrap flex-shrink-0"
                              style={{
                                backgroundColor: displayRole === 'editor' ? 'var(--info-amber-bg)' : 'var(--collab-blue-bg)',
                                color: displayRole === 'editor' ? 'var(--info-amber-text)' : 'var(--collab-blue-text)'
                              }}
                              title={
                                displayRole === 'viewer' ? t('share.roleViewerTooltip') :
                                displayRole === 'editor' ? t('share.roleEditorTooltip') :
                                displayRole === 'musician' ? t('share.roleMusicianTooltip') :
                                displayRole === 'sound_engineer' ? t('share.roleSoundEngineerTooltip') :
                                displayRole === 'content_maker' ? t('share.roleContentMakerTooltip') :
                                ''
                              }
                            >
                              {displayRole === 'editor' ? t('share.editor') : 
                               displayRole === 'musician' ? t('share.roleMusician') :
                               displayRole === 'sound_engineer' ? t('share.roleSoundEngineer') :
                               displayRole === 'content_maker' ? t('share.roleContentMaker') :
                               t('share.viewer')}
                            </span>
                            {share.status && share.status !== 'active' && (
                              <span
                                className="text-xs px-2 py-0.5 rounded whitespace-nowrap flex-shrink-0"
                                style={{ backgroundColor: 'var(--info-amber-bg)', color: 'var(--info-amber-text)' }}
                                title={share.inactiveReason}
                              >
                                {shareStatusLabel(share)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {canManageRoles && (
                        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                          <select
                            value={displayRole}
                            onChange={(e) => {
                              const newRole = e.target.value as 'viewer' | 'editor' | 'musician' | 'sound_engineer' | 'content_maker';
                              // Check if collaborator can be assigned editor role (only Editor requires Premium)
                              const effectiveTier = collaboratorUser?.effectiveTier || 'starter';
                              const canBeEditor = effectiveTier === 'artist' || effectiveTier === 'studio';
                              if (!canBeEditor && newRole === 'editor') {
                                toast.error(t('share.editorRequiresPremium'));
                                return;
                              }
                              // New roles (musician, sound_engineer, content_maker) can be assigned to Starter users
                              setPendingRoleChanges(prev => ({
                                ...prev,
                                [share.collaboratorUserId]: newRole
                              }));
                            }}
                            className="flex-1 sm:flex-initial px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--brand-from)] focus:border-transparent"
                            title={
                              displayRole === 'viewer' ? t('share.roleViewerTooltip') :
                              displayRole === 'editor' ? t('share.roleEditorTooltip') :
                              displayRole === 'musician' ? t('share.roleMusicianTooltip') :
                              displayRole === 'sound_engineer' ? t('share.roleSoundEngineerTooltip') :
                              displayRole === 'content_maker' ? t('share.roleContentMakerTooltip') :
                              ''
                            }
                          >
                            <option value="viewer" title={t('share.roleViewerTooltip')}>{t('share.viewer')}</option>
                            <option value="editor" disabled={(collaboratorUser?.effectiveTier || 'starter') === 'starter'} title={t('share.roleEditorTooltip')}>
                              {t('share.editor')} {(collaboratorUser?.effectiveTier || 'starter') === 'starter' && '🔒'}
                            </option>
                            <option value="musician" title={t('share.roleMusicianTooltip')}>{t('share.roleMusician')}</option>
                            <option value="sound_engineer" title={t('share.roleSoundEngineerTooltip')}>{t('share.roleSoundEngineer')}</option>
                            <option value="content_maker" title={t('share.roleContentMakerTooltip')}>{t('share.roleContentMaker')}</option>
                          </select>
                          {pendingRole && pendingRole !== currentRole && (
                            <button
                              onClick={() => handleRoleChange(share.collaboratorUserId, pendingRoleChanges[share.collaboratorUserId])}
                              disabled={loading}
                              className="px-3 py-1.5 text-sm rounded transition-colors disabled:opacity-50 whitespace-nowrap"
                              style={{ color: 'var(--collab-blue-text)' }}
                              onMouseEnter={(e) => {
                                const target = e.target as HTMLElement;
                                target.style.backgroundColor = 'var(--collab-blue-bg)';
                              }}
                              onMouseLeave={(e) => {
                                const target = e.target as HTMLElement;
                                target.style.backgroundColor = 'transparent';
                              }}
                            >
                              Update
                            </button>
                          )}
                          {canRevoke && (
                            <button
                              onClick={() => handleRevoke(share.collaboratorUserId)}
                              disabled={revokingUsers.has(share.collaboratorUserId)}
                              className="px-2 py-1.5 text-xs rounded transition-colors whitespace-nowrap"
                              style={{
                                color: revokingUsers.has(share.collaboratorUserId) ? 'var(--color-muted)' : 'var(--status-live-text)',
                                cursor: revokingUsers.has(share.collaboratorUserId) ? 'not-allowed' : 'pointer'
                              }}
                              onMouseEnter={(e) => {
                                if (!revokingUsers.has(share.collaboratorUserId)) {
                                  const target = e.target as HTMLElement;
                                  target.style.backgroundColor = 'var(--status-live-bg)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!revokingUsers.has(share.collaboratorUserId)) {
                                  const target = e.target as HTMLElement;
                                  target.style.backgroundColor = 'transparent';
                                }
                              }}
                            >
                              {revokingUsers.has(share.collaboratorUserId) ? t('share.revoking') : t('share.revoke')}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackCollaborators;
